/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
} from '@angular/core';

import { ID } from '@gh/common-shared-data';
import { GoogleApiLoader, GoogleAuthService } from '@gh/core-external';
import { PreventableEvent, UI_ERROR_HANDLER, UiErrorHandler } from '@gh/core-ui';
import { coerceStringArrayProperty } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';

import { difference, isNil, noop, uniq } from 'lodash';

import { FileListItem, FilePicker } from '../file-container/file-container.interfaces';
import { UserContext } from '@gh/common-shared';

export interface GoogleDocument {
  id: ID;
  serviceId: string;
  mimeType: string;
  name: string;
  description: string;
  type: string;
  lastEditedUtc: number;
  iconUrl: string;
  url: string;
  embedUrl: string;
  sizeBytes: number;
  isShared: boolean;
}

export interface FilePickerSelectedEvent {
  action: 'picked' | 'loaded' | 'cancel';
  viewToken: any[];
  docs: GoogleDocument[];
}

export enum GoogleFilePickerSelectionMode { Single = 'single', Multi = 'multi' }

export enum GoogleFilePickerView { SharedWithMe = 'sharedWithMe', Folder = 'folder', Upload = 'upload', MyDrive = 'myDrive' }

export type GoogleFilePickerSkin = 'flat' | 'raised' | 'icon' | 'file-input' | 'button';

const GOOGLE_FILE_PICKER_KNOWN_VIEWS = [
  GoogleFilePickerView.SharedWithMe,
  GoogleFilePickerView.Folder,
  GoogleFilePickerView.Upload,
  GoogleFilePickerView.MyDrive,
];

@Component({
  selector: 'gh-google-file-picker',
  template: require('./google-file-picker.component.html'),
  styles: [require('./google-file-picker.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'ghGoogleFilePicker',
})
export class GoogleFilePickerComponent implements FilePicker {
  @Input() skin: GoogleFilePickerSkin = 'flat';
  @Input() title: string;
  @Input() folderId: ID;
  @Input() selectionMode: GoogleFilePickerSelectionMode = GoogleFilePickerSelectionMode.Single;
  @Input() disabled: boolean = false;
  @Input() accept: string;
  @Input() validate?: TransformFn<GoogleDocument[], boolean>;

  @Output() preOpen = new EventEmitter<PreventableEvent>();
  @Output() selected = new EventEmitter<GoogleDocument[]>();

  files?: GoogleDocument[];

  private onPickerSelected: Function = noop;

  @Input()
  set views(views: string | string[]) {
    this._views = uniq(coerceStringArrayProperty(views));

    const unknownViews = difference(this._views, GOOGLE_FILE_PICKER_KNOWN_VIEWS);
    if (unknownViews.length > 0) {
      throw new Error(`GoogleFilePickerComponent: the following views are unknown: ${unknownViews.join(',')}`);
    }
  }

  get fileName(): string {
    const { files } = this;
    return files && files.length && files[0].name || '';
  }

  private _views: string[];

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private apiLoader: GoogleApiLoader,
              private userContext: UserContext,
              private googleAuthService: GoogleAuthService,
              @Inject(UI_ERROR_HANDLER) private errorHandler: UiErrorHandler) {

  }

  open(): void {
    const event = new PreventableEvent();
    this.preOpen.emit(event);
    if (!event.cancelled) {
      this.apiLoader.load('picker').pipe(map$(() => this.googleAuthService.onPickerLibLoaded()))
        .subscribe(
          () => this.googleAuthService.signIn(() => this.openPicker()),
          (error: any) => this.errorHandler.raise(error));
    }
  }

  onOpen(): void {
    this.open();
  }

  registerOnSelected(onSelected: (files: FileListItem[]) => void): void {
    this.onPickerSelected = onSelected;
  }

  private openPicker(): void {
    const { _views } = this;

    if (isNil(_views) || _views.length === 0) {
      throw new Error(`GoogleFilePickerComponent: no one view was defined`);
    }

    const pickerViews = _views.map((view) => {
      switch (view) {
        case GoogleFilePickerView.Folder: {
          const docsView = new google.picker.DocsView()
            .setIncludeFolders(true);
          if (this.folderId) {
            docsView.setParent(this.folderId);
          }
          if (this.accept) {
            docsView['setMimeTypes'](this.accept);
          }
          return docsView;
        }
        case GoogleFilePickerView.MyDrive: {
          const docsView = new google.picker.DocsView()
            .setOwnedByMe(true)
            .setIncludeFolders(true);
          if (this.accept) {
            docsView['setMimeTypes'](this.accept);
          }
          return docsView;
        }
        case GoogleFilePickerView.SharedWithMe: {
          const docsView = new google.picker.DocsView()
            .setOwnedByMe(false)
            .setIncludeFolders(true);
          if (this.accept) {
            docsView['setMimeTypes'](this.accept);
          }
          return docsView;
        }
        case GoogleFilePickerView.Upload:
          const docsUploadView = new google.picker.DocsUploadView();
          if (this.folderId) {
            docsUploadView.setParent(this.folderId);
          }
          return docsUploadView;
        default:
          throw new Error(`GoogleFilePickerComponent: unknown view ${view}`);
      }
    });

    const pickerBuilder = new google.picker.PickerBuilder();

    pickerViews.forEach((view) => pickerBuilder.addView(view));

    if (this.selectionMode === GoogleFilePickerSelectionMode.Multi) {
      pickerBuilder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    const picker = pickerBuilder
      .setOAuthToken(this.googleAuthService.accessToken)
      .setCallback(this.onPickerResult.bind(this))
      .build();

    picker.setVisible(true);
  }

  private onPickerResult(response: FilePickerSelectedEvent): void {
    if (response.action === 'picked') {
      // if validation was provided, run it.
      // validation can block selection
      if (this.validate && !this.validate(response.docs)) {
        return;
      }
      this.files = response.docs;
      this.onPickerSelected(response.docs.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.mimeType,
        url: doc.url,
        ownerId: this.userContext.data.login,
      })));
      this.selected.emit(response.docs);
      this.changeDetectorRef.markForCheck();
    }
  }
}
